import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AGENTS } from "@/lib/brand";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", agent: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and a short note.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you — a member of Watson Group will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "", agent: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email hello@watsongroup.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-[#0A0A0A] py-32 md:py-48 px-6 md:px-12 border-t border-white/5">
      <div className="mx-auto max-w-[1500px] grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
        <div className="md:col-span-6">
          <p className="overline text-[#CBBFAE] mb-6">Begin a Conversation</p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="font-serif text-6xl md:text-8xl leading-[0.9] tracking-tight text-white mb-10"
          >
            Let's<br />
            <em className="text-[#CBBFAE]">talk.</em>
          </motion.h2>
          <p className="text-white/60 max-w-md leading-relaxed">
            Whether you're considering a move next month or three years from now,
            the earliest conversations are often the most valuable ones.
          </p>

          <div className="mt-14 space-y-3 text-white/70 text-sm">
            <div className="flex gap-4"><span className="overline text-white/40 w-24 shrink-0">Emails</span>
              <div className="space-y-1">
                <div>angelawatsonhomes@gmail.com</div>
                <div>vorozco9719@gmail.com</div>
                <div>jacobflora.realtor@gmail.com</div>
              </div>
            </div>
            <div className="flex gap-4"><span className="overline text-white/40 w-24 shrink-0">Phones</span>
              <div className="space-y-1">
                <div>(209) 620-7575</div>
                <div>(209) 534-4374</div>
                <div>(209) 380-2306</div>
              </div>
            </div>
            <div className="flex gap-4"><span className="overline text-white/40 w-24 shrink-0">Instagram</span>
              <div className="space-y-1">
                <div>@angelawatsonhomes</div>
                <div>@vorozco61</div>
                <div>@jacobflora.realtor</div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="md:col-span-6 space-y-2">
          <input
            data-testid="contact-form-name-input"
            className="line-input"
            placeholder="Your name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <input
            data-testid="contact-form-email-input"
            type="email"
            className="line-input"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <input
            data-testid="contact-form-phone-input"
            className="line-input"
            placeholder="Phone (optional)"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <select
            data-testid="contact-form-agent-select"
            className="line-input appearance-none cursor-pointer"
            value={form.agent}
            onChange={(e) => setForm({ ...form, agent: e.target.value })}
          >
            <option value="" className="bg-[#121212]">Any agent — I'm not sure yet</option>
            {AGENTS.map((a) => <option key={a.id} value={a.name} className="bg-[#121212]">{a.name}</option>)}
          </select>
          <textarea
            data-testid="contact-form-message-input"
            className="line-input min-h-[120px] resize-none pt-6"
            placeholder="Tell us a little about what you're looking for."
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
          />
          <button
            data-testid="contact-form-submit-button"
            type="submit"
            disabled={submitting}
            className="mt-10 group inline-flex items-center gap-4 border border-[#CBBFAE]/50 hover:border-[#CBBFAE] rounded-full pl-6 pr-2 py-2 text-[12px] tracking-[0.25em] uppercase text-white transition-all disabled:opacity-50"
          >
            <span>{submitting ? "Sending…" : "Send Message"}</span>
            <span className="w-9 h-9 rounded-full bg-[#CBBFAE] text-[#0A0A0A] flex items-center justify-center transition-transform group-hover:rotate-45">↗</span>
          </button>
        </form>
      </div>
    </section>
  );
}
